import { useCallback, useEffect, useRef, useState } from 'react';
import * as Speech from 'expo-speech';

import { useEventCallback } from '../../../common/utils/hooks';

// Voice picking: reads each displayed item aloud, and "Next" ticks the current
// item before moving on. Announcements wait while a tub change is unacknowledged.
const usePickListVoiceMode = ({
	displayedItems,
	scrollViewRef,
	pickListItemToggleIsPicked,
	setPickListItemIsPickedBulk,
	tubWeightDisplay,
	pendingTubSwitchIndex,
	highestAcknowledgedTubIndexRef,
}) => {
	const [currentVoiceItemIndex, setCurrentVoiceItemIndex] = useState(-1);
	const [isVoiceMode, setIsVoiceMode] = useState(false);
	const [queuedVoiceItem, setQueuedVoiceItem] = useState(null);
	const voiceItemsRef = useRef([]);

	const quitVoiceMode = useCallback(() => {
		setIsVoiceMode(false);
		setCurrentVoiceItemIndex(-1);
		setQueuedVoiceItem(null);
		voiceItemsRef.current = [];
	}, [setIsVoiceMode, setCurrentVoiceItemIndex]);

	const startVoiceMode = useCallback(() => {
		if (currentVoiceItemIndex === -1) {
			voiceItemsRef.current = displayedItems || [];
		}
		setIsVoiceMode(true);
	}, [currentVoiceItemIndex, displayedItems]);

	const speakVoiceItem = useEventCallback(async (item) => {
		if (!item) {
			return;
		}

		if (await Speech.isSpeakingAsync()) {
			await Speech.stop();
		}
		Speech.speak(item.product.audioName || item.product.name);
		Speech.speak(String(item.requiredQuantity));
	});

	const onPressNext = useCallback(async () => {
		const voiceItems = voiceItemsRef.current;
		if (!voiceItems.length) {
			quitVoiceMode();
			return;
		}

		// stop if currently speaking
		if (await Speech.isSpeakingAsync()) {
			await Speech.stop();
		}
		// mark current item as picked
		if (currentVoiceItemIndex > -1) {
			const currItem = voiceItems[currentVoiceItemIndex];
			// handle batched items
			if (Array.isArray(currItem.id)) {
				await setPickListItemIsPickedBulk(currItem.product.id, true);
			}
			else {
				await pickListItemToggleIsPicked(currItem.id);
			}
		}

		const nextIndex = currentVoiceItemIndex + 1;

		if (nextIndex > voiceItems.length - 1) {
			quitVoiceMode();
			return;
		}

		scrollViewRef.current?.scrollTo({ y: nextIndex * 60 });

		setCurrentVoiceItemIndex(nextIndex);
		setQueuedVoiceItem(voiceItems[nextIndex]);
	}, [quitVoiceMode, currentVoiceItemIndex, setPickListItemIsPickedBulk, pickListItemToggleIsPicked, scrollViewRef]);

	const repeatVoiceItem = useCallback(() => {
		speakVoiceItem(voiceItemsRef.current[currentVoiceItemIndex]);
	}, [currentVoiceItemIndex, speakVoiceItem]);

	const pauseVoiceMode = useCallback(() => {
		setIsVoiceMode(false);
	}, []);

	useEffect(() => {
		const currentTubIndex = tubWeightDisplay?.currentTubIndex || 0;
		const highestCalculatedTubIndex = tubWeightDisplay?.needsNextTubPrompt && tubWeightDisplay?.nextTubIndex
			? tubWeightDisplay.nextTubIndex
			: currentTubIndex;
		const hasUnacknowledgedTubSwitch = highestCalculatedTubIndex > (highestAcknowledgedTubIndexRef.current || 0);

		if (!isVoiceMode || !queuedVoiceItem || pendingTubSwitchIndex !== null || hasUnacknowledgedTubSwitch) {
			return undefined;
		}

		let isActive = true;
		const announceVoiceItem = async () => {
			await speakVoiceItem(queuedVoiceItem);
			if (isActive) {
				setQueuedVoiceItem(null);
			}
		};
		announceVoiceItem();

		return () => {
			isActive = false;
		};
	}, [
		isVoiceMode,
		pendingTubSwitchIndex,
		queuedVoiceItem,
		speakVoiceItem,
		tubWeightDisplay?.currentTubIndex,
		tubWeightDisplay?.needsNextTubPrompt,
		tubWeightDisplay?.nextTubIndex,
		highestAcknowledgedTubIndexRef,
	]);

	return {
		isVoiceMode,
		currentVoiceItemIndex,
		startVoiceMode,
		onPressNext,
		repeatVoiceItem,
		pauseVoiceMode,
		quitVoiceMode,
	};
};

export default usePickListVoiceMode;
